// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';

export type DateOrderErrors = Partial<
  Record<number, { start?: string; end?: string }>
>;

interface LineItemDateInfo {
  startOn: DateTime | null;
  endOn: DateTime | null;
}

interface FieldGroup {
  groupKey: string;
  indices: number[];
}

export const MIN_YEAR = 1970;
export const MAX_YEAR = 2099;
export const YEAR_RANGE_ERROR = `Year must be between ${String(MIN_YEAR)} and ${String(MAX_YEAR)}`;

/** Finds the phases in each group that overlap the phase before them or leave a gap after it. */
export function findPhaseSequenceErrors(
  fieldGroups: FieldGroup[],
  lineItems: (LineItemDateInfo | undefined)[]
): DateOrderErrors {
  const errors: DateOrderErrors = {};

  for (const group of fieldGroups) {
    if (group.indices.length < 2) continue;

    // Phases are compared in start date order, as the server does, so rows entered out of order are not refused.
    const orderedIndices = [...group.indices].sort((a, b) => {
      const aStart = lineItems[a]?.startOn;
      const bStart = lineItems[b]?.startOn;
      const hasAStart = aStart?.isValid === true;
      const hasBStart = bStart?.isValid === true;
      if (!hasAStart || !hasBStart) {
        return Number(hasAStart) - Number(hasBStart);
      }

      return aStart.toMillis() - bStart.toMillis();
    });

    for (let pos = 1; pos < orderedIndices.length; pos++) {
      const prevIdx = orderedIndices[pos - 1];
      const currIdx = orderedIndices[pos];
      const prev = lineItems[prevIdx];
      const curr = lineItems[currIdx];

      const prevEnd = prev?.endOn ?? null;
      const prevStart = prev?.startOn ?? null;
      const currStart = curr?.startOn ?? null;

      // Two phases in a group can never start on the same day
      if (currStart && prevStart && currStart <= prevStart) {
        const existingErrors = errors[currIdx] ?? {};
        if (!existingErrors.start) {
          errors[currIdx] = {
            ...existingErrors,
            start: 'Start date cannot match another phase in the group',
          };
        }
      }

      // Continuity checks: within a group, currStart must equal prevEnd + 1 day
      if (prevEnd && currStart && !errors[currIdx]?.start) {
        const expectedStart = prevEnd.plus({ days: 1 });
        if (currStart < expectedStart) {
          // Overlap: current item starts on or before the previous item's end
          errors[currIdx] = {
            ...errors[currIdx],
            start: 'Start date overlaps with the previous phase',
          };
          errors[prevIdx] = {
            ...errors[prevIdx],
            end: 'End date overlaps with the next phase',
          };
        } else if (currStart > expectedStart) {
          // Gap: current item starts more than 1 day after previous end
          const existingErrors = errors[currIdx] ?? {};
          if (!existingErrors.start) {
            errors[currIdx] = {
              ...existingErrors,
              start:
                'Gap detected — start date must be the day after the previous phase ends',
            };
          }
        }
      }
    }
  }

  return errors;
}

/**
 * Validates date ordering, continuity, and ranges within aggregation groups.
 * Returns per-index error messages for start and end dates.
 *
 * Rules:
 * - No overlaps within a group (start must be on or after previous end)
 * - No gaps within a group (start must be exactly previous end + 1 day)
 * - Each item's end must be on or after its own start
 * - All years must be between 1970 and 2099
 */
export function validateLineItemDates(
  fieldGroups: FieldGroup[],
  lineItems: (LineItemDateInfo | undefined)[]
): DateOrderErrors {
  const errors = findPhaseSequenceErrors(fieldGroups, lineItems);

  // Year range and self-consistency for all items
  for (let i = 0; i < lineItems.length; i++) {
    const item = lineItems[i];
    const itemStart = item?.startOn ?? null;
    const itemEnd = item?.endOn ?? null;

    if (itemStart && (itemStart.year < MIN_YEAR || itemStart.year > MAX_YEAR)) {
      const prev = errors[i] ?? {};
      if (!prev.start) {
        errors[i] = { ...prev, start: YEAR_RANGE_ERROR };
      }
    }

    if (itemEnd && (itemEnd.year < MIN_YEAR || itemEnd.year > MAX_YEAR)) {
      const prev = errors[i] ?? {};
      if (!prev.end) {
        errors[i] = { ...prev, end: YEAR_RANGE_ERROR };
      }
    }

    if (itemStart && itemEnd && itemEnd < itemStart && !(errors[i] ?? {}).end) {
      errors[i] = {
        ...errors[i],
        end: 'End date is before the start date',
      };
    }
  }

  return errors;
}
